interface GroqConfig {
  apiKey: string;
  baseURL?: string;
}

interface GroqTTSOptions {
  maxLength?: number;
  style?: 'summary' | 'simplified' | 'engaging' | 'technical';
  language?: string;
  includeKeyPoints?: boolean;
  voice?: string;
  responseFormat?: 'wav';
}

interface GroqResponse {
  enhancedText: string;
  keyPoints?: string[];
  estimatedDuration?: number;
}

interface GroqAudioResponse {
  audioBlob: Blob;
  enhancedText: string;
  estimatedDuration: number;
  keyPoints?: string[];
}

// Available voices for Groq PlayAI TTS
export const GROQ_ENGLISH_VOICES = [
  'Arista-PlayAI', 'Atlas-PlayAI', 'Basil-PlayAI', 'Briggs-PlayAI',
  'Calum-PlayAI', 'Celeste-PlayAI', 'Cheyenne-PlayAI', 'Chip-PlayAI',
  'Cillian-PlayAI', 'Deedee-PlayAI', 'Fritz-PlayAI', 'Gail-PlayAI',
  'Indigo-PlayAI', 'Mamaw-PlayAI', 'Mason-PlayAI', 'Mikail-PlayAI',
  'Mitch-PlayAI', 'Quinn-PlayAI', 'Thunder-PlayAI'
] as const;

export const GROQ_ARABIC_VOICES = [
  'Ahmad-PlayAI', 'Amira-PlayAI', 'Khalid-PlayAI', 'Nasser-PlayAI'
] as const;

export type GroqEnglishVoice = typeof GROQ_ENGLISH_VOICES[number];
export type GroqArabicVoice = typeof GROQ_ARABIC_VOICES[number];

class GroqTTSService {
  private apiKey: string;
  private baseURL: string;

  constructor(config: GroqConfig) {
    this.apiKey = config.apiKey;
    this.baseURL = config.baseURL || 'https://api.groq.com/openai/v1';
  }

  /**
   * Generate high-quality audio using Groq's PlayAI TTS
   */
  async generateAudio(
    text: string,
    title: string,
    options: GroqTTSOptions = {}
  ): Promise<GroqAudioResponse> {
    const {
      maxLength = 500,
      style = 'engaging',
      language = 'en',
      voice = 'Celeste-PlayAI',
      responseFormat = 'wav'
    } = options;

    try {
      // First, enhance the text using Groq's chat completion
      const enhancedResponse = await this.enhanceTextForTTS(text, title, options);
      const finalText = enhancedResponse.enhancedText;

      // Determine the model based on language and voice
      const isArabic = language.startsWith('ar') || GROQ_ARABIC_VOICES.includes(voice as any);
      const model = isArabic ? 'playai-tts-arabic' : 'playai-tts';

      // Generate audio using Groq PlayAI TTS
      const response = await fetch(`${this.baseURL}/audio/speech`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model,
          voice,
          input: finalText,
          response_format: responseFormat
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Groq TTS API error: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const audioBlob = await response.blob();

      return {
        audioBlob,
        enhancedText: finalText,
        estimatedDuration: this.estimateDuration(finalText),
        keyPoints: enhancedResponse.keyPoints
      };

    } catch (error) {
      console.error('Error generating audio with Groq:', error);
      throw error;
    }
  }

  /**
   * Generate audio with automatic voice selection based on content
   */
  async generateSmartAudio(
    text: string,
    title: string,
    contentType: 'scientific' | 'cultural' | 'educational' | 'storytelling' = 'educational'
  ): Promise<GroqAudioResponse> {
    // Smart voice selection based on content type
    const voiceMap = {
      scientific: 'Atlas-PlayAI',      // Professional, clear
      cultural: 'Celeste-PlayAI',      // Warm, engaging
      educational: 'Fritz-PlayAI',     // Clear, friendly
      storytelling: 'Cheyenne-PlayAI'  // Expressive, narrative
    };

    const voice = voiceMap[contentType];

    return this.generateAudio(text, title, {
      voice,
      style: contentType === 'storytelling' ? 'engaging' : 'simplified',
      maxLength: 600
    });
  }

  /**
   * Enhance text using Groq for better TTS output
   */
  async enhanceTextForTTS(
    text: string, 
    title: string, 
    options: GroqTTSOptions = {}
  ): Promise<GroqResponse> {
    const {
      maxLength = 500,
      style = 'summary',
      language = 'en',
      includeKeyPoints = false
    } = options;

    try {
      const prompt = this.buildPrompt(text, title, style, maxLength, language, includeKeyPoints);
      
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'llama-3.1-8b-instant', // Fast and capable model
          messages: [
            {
              role: 'system',
              content: 'You are an expert at creating engaging, clear text optimized for text-to-speech conversion. Focus on natural speech patterns, clear pronunciation, and listener engagement.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: Math.min(maxLength * 2, 1500),
          temperature: 0.7,
          stream: false
        })
      });

      if (!response.ok) {
        throw new Error(`Groq API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const enhancedContent = data.choices[0]?.message?.content;

      if (!enhancedContent) {
        throw new Error('No content received from Groq API');
      }

      return this.parseGroqResponse(enhancedContent, maxLength);

    } catch (error) {
      console.error('Error enhancing text with Groq:', error);
      // Fallback to simple truncation
      return {
        enhancedText: text.length > maxLength ? text.substring(0, maxLength) + '...' : text,
        estimatedDuration: this.estimateDuration(text)
      };
    }
  }

  /**
   * Generate audio-optimized summary
   */
  async generateAudioSummary(
    content: string, 
    title: string, 
    targetDuration: number = 60
  ): Promise<GroqAudioResponse> {
    const maxLength = Math.floor(targetDuration * 8); // ~8 words per second average
    
    return this.generateAudio(content, title, {
      maxLength,
      style: 'engaging',
      includeKeyPoints: true,
      voice: 'Celeste-PlayAI' // Warm, engaging voice for summaries
    });
  }

  /**
   * Translate and generate audio for different languages
   */
  async translateAndGenerateAudio(
    text: string, 
    title: string, 
    targetLanguage: string
  ): Promise<GroqAudioResponse> {
    if (targetLanguage.startsWith('ar')) {
      // Use Arabic model
      const translatedResponse = await this.translateForTTS(text, title, targetLanguage);
      return this.generateAudio(translatedResponse.enhancedText, title, {
        voice: 'Amira-PlayAI', // Default Arabic voice
        language: targetLanguage
      });
    }
    
    // For other languages, translate first then use English TTS
    const translatedResponse = await this.translateForTTS(text, title, targetLanguage);
    return this.generateAudio(translatedResponse.enhancedText, title, {
      voice: 'Fritz-PlayAI'
    });
  }

  /**
   * Translate and optimize for TTS
   */
  async translateForTTS(
    text: string, 
    title: string, 
    targetLanguage: string
  ): Promise<GroqResponse> {
    const prompt = `Translate the following text to ${targetLanguage} and optimize it for text-to-speech:

Title: ${title}
Content: ${text}

Requirements:
- Natural, conversational tone
- Clear pronunciation
- Cultural appropriateness
- Maximum 400 words
- Include pronunciation notes for difficult words if needed`;

    try {
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'llama-3.1-8b-instant',
          messages: [
            {
              role: 'system',
              content: `You are an expert translator who specializes in creating text optimized for text-to-speech in ${targetLanguage}.`
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 1000,
          temperature: 0.3,
          stream: false
        })
      });

      const data = await response.json();
      const translatedContent = data.choices[0]?.message?.content;

      return {
        enhancedText: translatedContent || text,
        estimatedDuration: this.estimateDuration(translatedContent || text)
      };

    } catch (error) {
      console.error('Error translating with Groq:', error);
      return {
        enhancedText: text,
        estimatedDuration: this.estimateDuration(text)
      };
    }
  }

  private buildPrompt(
    text: string, 
    title: string, 
    style: string, 
    maxLength: number, 
    language: string, 
    includeKeyPoints: boolean
  ): string {
    const styleInstructions = {
      summary: 'Create a concise, informative summary',
      simplified: 'Simplify the language while maintaining accuracy',
      engaging: 'Make it engaging and conversational for audio listeners',
      technical: 'Maintain technical accuracy but improve readability'
    };

    let prompt = `${styleInstructions[style as keyof typeof styleInstructions]} of the following content for text-to-speech conversion:

Title: ${title}
Content: ${text}

Requirements:
- Maximum ${maxLength} words
- Natural speech patterns
- Clear, pronounceable text
- Engaging for audio listeners
- Language: ${language}`;

    if (includeKeyPoints) {
      prompt += '\n- Include 2-3 key takeaways at the end';
    }

    prompt += '\n\nReturn only the optimized text, ready for text-to-speech conversion.';

    return prompt;
  }

  private parseGroqResponse(content: string, maxLength: number): GroqResponse {
    // Try to extract key points if they exist
    const keyPointsMatch = content.match(/(?:key (?:points|takeaways)|main points):\s*(.*?)(?:\n\n|$)/is);
    let keyPoints: string[] = [];
    let enhancedText = content;

    if (keyPointsMatch) {
      const keyPointsText = keyPointsMatch[1];
      keyPoints = keyPointsText
        .split(/[•\-\*\n]/)
        .map(point => point.trim())
        .filter(point => point.length > 0)
        .slice(0, 3);
      
      // Remove key points from main text
      enhancedText = content.replace(keyPointsMatch[0], '').trim();
    }

    // Ensure we don't exceed max length
    if (enhancedText.length > maxLength) {
      enhancedText = enhancedText.substring(0, maxLength);
      const lastSentence = enhancedText.lastIndexOf('.');
      if (lastSentence > maxLength * 0.8) {
        enhancedText = enhancedText.substring(0, lastSentence + 1);
      } else {
        enhancedText += '...';
      }
    }

    return {
      enhancedText,
      keyPoints: keyPoints.length > 0 ? keyPoints : undefined,
      estimatedDuration: this.estimateDuration(enhancedText)
    };
  }

  private estimateDuration(text: string): number {
    // Approximate: 150-160 words per minute for TTS
    const words = text.split(/\s+/).length;
    return Math.ceil((words / 155) * 60); // seconds
  }

  /**
   * Test the Groq API connection
   */
  async testConnection(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'llama-3.1-8b-instant',
          messages: [
            {
              role: 'user',
              content: 'Test connection. Reply with "OK"'
            }
          ],
          max_tokens: 10,
          temperature: 0
        })
      });

      return response.ok;
    } catch (error) {
      console.error('Groq connection test failed:', error);
      return false;
    }
  }

  /**
   * Get available voices for the current language
   */
  getAvailableVoices(language: string = 'en'): string[] {
    if (language.startsWith('ar')) {
      return [...GROQ_ARABIC_VOICES];
    }
    return [...GROQ_ENGLISH_VOICES];
  }
}

export default GroqTTSService;
export type { GroqConfig, GroqTTSOptions, GroqResponse, GroqAudioResponse };
